import {record, array, merge, nullable, string, number, Parser, type ParserT} from '@freckle/parser'
import {type IdentityProviderT} from '@frontrow/console/src/entities/helpers/identity-provider'
import {
  type AssertNoUnacceptedKeys,
  type CamelCaseKeysOnly,
  type FancyOk,
  type fancyOperations,
  type UnacceptedKeys
} from '@frontrow/console/src/open-api/clients'
import {
  parseSchoolAttrs,
  type SchoolAttrs,
  type RenaissanceSchoolClientT,
  type StarStatus
} from '@frontrow/console/src/entities/models/school'

type WeakRenaissanceSchoolClientT = {
  platform: string | undefined | null
  clientId: string | undefined | null
  rpid: string | undefined | null
  baseUrl: string | undefined | null
  starStatus: StarStatus
  sharedRosteringEnabled: boolean
}

type BasicSchoolT = {
  name: string
  city: string
  administrativeArea: string | undefined | null
  postalCode: string | undefined | null
  formattedAddressLines: Array<string> | undefined | null
  countryCode: string | undefined | null
  districtId: number
  sisId: string | undefined | null
  latLong: LatLong | undefined | null
  cleverSchoolId: string | undefined | null
  classLinkSourceId: string | undefined | null
  renaissanceRpIdentifier: string | undefined | null
  licenseUtilizationNotificationsMutedAt: Date | undefined | null
  courseSubjectsByLicensing: boolean
  activeIdp: IdentityProviderT | undefined | null
  syncIdp: boolean
  crmId: string | undefined | null
}

type BasicSchoolWithRenaissanceClientT = {
  renaissanceSchoolClient?: RenaissanceSchoolClientT | null
} & BasicSchoolT

/**
 * Hand-written: `FancyBody<patchSchoolR>` types every key optional, while the
 * edit form always holds `name`, `city`, `districtId`, … The key set is pinned.
 */
type _SchoolPatchKeysAreAccepted = AssertNoUnacceptedKeys<
  UnacceptedKeys<BasicSchoolWithRenaissanceClientT, fancyOperations['patchSchoolR']>
>

type DistrictName = {
  districtName: string | undefined | null
}

type LatLong = {
  latitude?: number | null
  longitude?: number | null
}

type SchoolDetailsT = DistrictName & LatLong
type DetailedSchoolT = SchoolAttrs & SchoolDetailsT

const parseSchoolDetails: ParserT<SchoolDetailsT> = record({
  districtName: nullable(string()),
  latitude: nullable(number()),
  longitude: nullable(number())
})

const detailedSchoolParser: ParserT<DetailedSchoolT> = merge(parseSchoolAttrs, parseSchoolDetails)

const detailedSchoolsParser: ParserT<Array<DetailedSchoolT>> = array(detailedSchoolParser)

function parse(
  response: CamelCaseKeysOnly<FancyOk<fancyOperations['getDistrictSchoolsR']>[number]>
): SchoolAttrs {
  return Parser.run(response, parseSchoolAttrs)
}

// `getDistrictSchoolsR` and `deleteSchoolR` omit `districtName`; use `parse`.
function parseDetailedSchool(
  response: CamelCaseKeysOnly<FancyOk<fancyOperations['getSchoolR']>>
): DetailedSchoolT {
  return Parser.run(response, detailedSchoolParser)
}

function parseDetailedSchools(
  response: Array<CamelCaseKeysOnly<FancyOk<fancyOperations['getSchoolR']>>>
): Array<DetailedSchoolT> {
  return Parser.run(response, detailedSchoolsParser)
}

function detailedToBasicSchool(detailedSchool: DetailedSchoolT): BasicSchoolT {
  const {
    name,
    city,
    administrativeArea,
    postalCode,
    formattedAddressLines,
    countryCode,
    districtId,
    sisId,
    latitude,
    longitude,
    cleverSchoolId,
    renaissanceRpIdentifier,
    licenseUtilizationNotificationsMutedAt,
    courseSubjectsByLicensing,
    activeIdp,
    syncIdp,
    crmId
  } = detailedSchool
  const latLong =
    latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined
      ? {latitude, longitude}
      : null
  return {
    name,
    city,
    administrativeArea,
    postalCode,
    formattedAddressLines,
    countryCode,
    districtId,
    sisId,
    latLong,
    cleverSchoolId,
    classLinkSourceId: null,
    renaissanceRpIdentifier,
    licenseUtilizationNotificationsMutedAt,
    courseSubjectsByLicensing,
    activeIdp,
    syncIdp,
    crmId
  }
}

// Useful information to know about schools before deletion
type SchoolDeletionInfoT = FancyOk<fancyOperations['getSchoolDeletionInfoR']>

export {parse, parseDetailedSchool, parseDetailedSchools, detailedToBasicSchool}
export type {
  BasicSchoolT,
  BasicSchoolWithRenaissanceClientT,
  SchoolAttrs,
  DetailedSchoolT,
  RenaissanceSchoolClientT,
  WeakRenaissanceSchoolClientT,
  SchoolDeletionInfoT
}
